import { postProfileImgsV2 } from '#api/accounts';
import { ImageUpload } from '#assets/svg';
import { Button, Flex, Input, Textarea } from '#atoms';
import usePatchCurrentStepMutation from '#hooks/queries/onboard/usePatchCurrentStepMutation';
import { FloatButtonContainer } from '#molecules';
import { useOnBoardStep } from '#pages/OnBoardStep/OnBoardStep.hooks';
import { useToast } from '#providers/ToastProvider';
import { useUserInfo } from '#providers/UserInfo';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { Controller, useForm } from 'react-hook-form';
import { InputLength } from 'src/shared/components/InputLength';
import { FilUploadWithMain } from 'src/shared/components/file-upload/FilUploadWithMain';
import styled from 'styled-components';

type Inputs = {
  shortIntroduction: string;
  introduce: string;
};

const maxCount = 10;
const maxCountMsg = '사진은 최대 10장까지 업로드 가능합니다.';

export default function Step7() {
  const { mutateAsync: patchCurrentStep } = usePatchCurrentStepMutation();
  const { setStep } = useOnBoardStep();

  const { userInfo, setUserInfo, isPatching, patchProfileAsync } =
    useUserInfo();
  const { showToast } = useToast();
  const { control, watch, handleSubmit, setValue } = useForm<Inputs>({
    defaultValues: {
      shortIntroduction: ''
    }
  });

  const [shortIntroduction, introduce] = watch([
    'shortIntroduction',
    'introduce'
  ]);

  const [mediaFileIdList, setMediaFileIdList] = useState<string[]>([]);

  const onSubmit = async (inputs: Inputs) => {
    try {
      await patchProfileAsync({
        shortIntroduction: inputs.shortIntroduction,
        introduction: inputs.introduce
      });

      await postProfileImgsV2(mediaFileIdList);
      TagManager.dataLayer({
        dataLayer: {
          event: 'onboarding_profilecomplete_click',
          category: '신규가입 온보딩',
          action: 'onboarding_profilecomplete_click',
          value: 2
        }
      });

      patchCurrentStep(8);
      setStep(8);
    } catch (err: any) {
      showToast({ type: 'fail', message: '프로필 등록에 실패하였습니다.' });
    }
  };

  useEffect(() => {
    if (!userInfo) return;

    setValue('shortIntroduction', userInfo.shortIntroduction ?? '');
    setValue('introduce', userInfo.introduction ?? '');
  }, [userInfo]);

  // 파일 업로드 완료 시 호출되는 함수
  const handleUploadFileComplete = (id: string) => {
    setMediaFileIdList(prev => [...prev, id]);
  };

  // 파일 삭제 완료 시 호출되는 함수
  const handleDeleteFileComplete = (id: string) => {
    setMediaFileIdList(prev => prev.filter(fileId => fileId !== id));
  };

  return (
    <>
      <Wrapper>
        <Title>나를 잘 보여줄 수 있는{'\n'}소개글을 작성해 주세요</Title>
        <Description>
          인스타그램 등 개인 SNS에 홍보할 수 있는{'\n'}프로필 링크에 표시됩니다.
        </Description>

        <Flex direction="column" gap={40}>
          <Flex direction="column" gap={8}>
            <Label
              style={{
                justifyContent: 'space-between'
              }}
            >
              <div>
                한 줄 소개{' '}
                <RequiredMark style={{ fontSize: '16px', marginLeft: '-4px' }}>
                  *
                </RequiredMark>
              </div>
              <InputLength
                currentLength={shortIntroduction?.length}
                maxLength={30}
              />
            </Label>
            <Controller
              control={control}
              name="shortIntroduction"
              render={({ field: { value, onChange } }) => (
                <Input
                  type="text"
                  placeholder="나를 가장 잘 표현할 수 있는 한 줄 소개"
                  value={value}
                  maxLength={18}
                  onChange={e => onChange(String(e))}
                />
              )}
            />

            {/* <Text
              textStyle="c1"
              textColor="gray2"
              style={{ textAlign: 'right' }}
            >{`${shortIntroduction.length} / 30`}</Text> */}
          </Flex>

          <Flex direction="column" gap={8}>
            <Label
              style={{
                justifyContent: 'space-between'
              }}
            >
              <div>
                소개글{' '}
                <RequiredMark style={{ fontSize: '16px', marginLeft: '-4px' }}>
                  *
                </RequiredMark>
              </div>
              <InputLength currentLength={introduce?.length} maxLength={1000} />
            </Label>
            <Controller
              control={control}
              name="introduce"
              render={({ field: { value, onChange } }) => (
                <Textarea
                  value={value ?? ''}
                  onChange={e => onChange(e)}
                  height={12.8}
                  limit={1000}
                  placeholder="본인의 트레이닝 경험을 기반으로 회원들에게 더 보여주고 싶은 내용을 담아보세요."
                />
              )}
            />
          </Flex>

          <Flex direction="column" gap={8}>
            <Label>
              <div>
                사진및 영상 업로드
                <RequiredMark style={{ fontSize: '16px', marginLeft: '0px' }}>
                  *
                </RequiredMark>
              </div>
            </Label>
            <FilUploadWithMain
              maxCountMsg={maxCountMsg}
              path={'profile'}
              fileType="image/*,.mp4,.mov"
              // onChange={onChange}
              maxCount={maxCount}
              onUploadFileComplete={handleUploadFileComplete}
              onDeleteFileComplete={handleDeleteFileComplete}
            >
              <ImageUpload />
            </FilUploadWithMain>
            {/* <Label>사진 업로드 </Label>
            <ImageWithUpload2
              postedImages={postedImgs}
              localImages={localImgs}
              onUploadImage={handleUploadImages}
              onDeleteLocalImage={handleDeleteLocalImage}
              onDeletePostedImage={handleDeletePostedImage}
              maxCount={maxCount}
              fileType={videoFileType}
            /> */}
            <InfoWrapper>
              <Flex gap={8} align="center">
                <Dot />
                <Info>사진은 최대 10장까지 업로드 가능합니다.</Info>
              </Flex>
              <Flex gap={8} align="center">
                <Dot />
                <Info>이미지를 드래그 하여 대표사진을 설정할 수 있습니다.</Info>
              </Flex>
            </InfoWrapper>
          </Flex>
        </Flex>
      </Wrapper>
      <FloatButtonContainer>
        <Flex gap={8}>
          {/* <Button background="anti" onClick={handleNextTime}>
            다음에 할게요
          </Button> */}
          <Button
            disabled={
              !shortIntroduction ||
              !introduce ||
              mediaFileIdList.length === 0 ||
              isPatching
            }
            onClick={handleSubmit(onSubmit)}
          >
            다음으로
          </Button>
        </Flex>
      </FloatButtonContainer>
    </>
  );
}

const Wrapper = styled.section``;

const Title = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: ${({ theme }) => theme.palette.gray15};
  white-space: pre-wrap;
  line-height: 30px;
`;

const Strong = styled.span`
  color: ${({ theme }) => theme.palette.brand300};
  line-height: 30px;
`;

const Description = styled.div`
  margin-top: 24px;
  margin-bottom: 56px;
  color: ${({ theme }) => theme.palette.gray100};
  white-space: pre-wrap;
`;

export const Label = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.gray25};
`;

// export const RequiredMark = styled.div`
//   display: inline-block;
//   font-weight: 500;
//   color: ${({ theme }) => theme.palette.brand300};
// `;

export const RequiredMark = styled.div`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  margin-left: auto;
  color: ${({ theme }) => theme.palette.brand300};
`;

export const Dot = styled.div`
  display: inline-block;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  font-weight: 500;
  background-color: ${({ theme }) => theme.palette.gray100};
`;

export const InfoWrapper = styled.div`
  margin-top: 4px;
`;

export const Info = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.gray100};
`;

interface SpinnerProps {
  size?: string; // 사이즈를 프로퍼티로 받아서 조절할 수 있도록 함
}
